import { HttpClient } from "@angular/common/http";
import { environment as env } from "../../../../../environments/environment.prod";
import { Injectable } from "@angular/core";
import { AbstractService } from "app/services/abstract.service";
import { Observable } from "rxjs";
import { IAPIListResult, IPagination, IPaginationResult, IRequestResult } from "app/common/interfaces";
import { andWhere, buildQuery, FilterFn, Ops, orderBy, OrderVariant, orWhere, page, where, select, noop } from "@albatrosdeveloper/ave-utils-npm/lib/utils/query.util";
import { StatusEnum } from "@albatrosdeveloper/ave-models-package/lib/enums";
import { ICreateBank } from "./interfaces/create-bank.interface";
import { IUpdateBank } from "./interfaces/update-bank.interface";
import { BankAttributes } from "@albatrosdeveloper/ave-models-package";

const apiUrl = env.API_PAYMENT_MASTER_URL;
const moduleName = "banks";

@Injectable({
  providedIn: "root",
})
export class BankService extends AbstractService<BankAttributes, ICreateBank, IUpdateBank> {
  constructor(
    http: HttpClient,
  ) {
    super(apiUrl, moduleName, http);
  }

  /**
   * Obtiene bancos con paginación y filtros.
   *
   * Estrategia de búsqueda:
   * - Sin búsqueda: Un solo objeto con filtros base
   * - Con búsqueda: Dos objetos (OR lógico) - busca en 'code', 'name'
   *
   * Cada objeto del OR debe incluir TODAS las condiciones base para mantener consistencia.
   *
   * @param options Pagination options
   * @param status Status filter
   * @param type Type filter
   * @param companyId Company filter
   * @returns Paginated data
   *
   * Search fields: code, name
   * Array filters: status, type
   * Single filters: companyId
   */
  getPaginateAllBank(
    options: IPagination<BankAttributes>,
    status?,
    type?,
    companyId?
  ): Observable<IPaginationResult<BankAttributes>> {

    const hasSearch = !!options.search;

    /**
     * Construye las condiciones base que SIEMPRE deben aplicarse.
     * Incluye: _deleted=false, filtro de compañía, estados, tipos.
     *
     * @param useOrWhere - Si es true, inicia un nuevo objeto con orWhere (para el OR lógico)
     */
    const buildBaseFilters = (params: { useOrWhere: boolean }): FilterFn<BankAttributes>[] => {
      const deletedFilter: FilterFn<BankAttributes> = params.useOrWhere
        ? orWhere('_deleted', Ops.eq(false))  // Inicia nuevo objeto en el array
        : where('_deleted', Ops.eq(false));   // Primer objeto del array

      return [
        deletedFilter,
        companyId
          ? andWhere('company.id' as keyof BankAttributes, Ops.eq(companyId))
          : noop(),
        status && status.length > 0
          ? andWhere('statusId', Ops.in(...status))
          : noop(),
        type && type.length > 0
          ? andWhere('type', Ops.in(...type))
          : noop()
      ];
    };

    // ========== CONSTRUCCIÓN DE OBJETOS JSON ==========

    // Objeto 1: Condiciones base + búsqueda por CODE (o solo condiciones si no hay búsqueda)
    const searchByCodeQueryObject: FilterFn<BankAttributes>[] = [
      ...buildBaseFilters({ useOrWhere: false }),  // false = usa 'where' (primer objeto)
      hasSearch ? andWhere("code", Ops.like(options.search!, "i")) : noop()
    ];

    // Objeto 2: Condiciones base + búsqueda por NAME (solo si hay búsqueda)
    const searchByNameQueryObject: FilterFn<BankAttributes>[] = hasSearch ? [
      ...buildBaseFilters({ useOrWhere: true }),   // true = usa 'orWhere' (segundo objeto)
      andWhere("name", Ops.like(options.search!, "i"))
    ] : [];

    // ========== ENSAMBLAJE FINAL ==========

    const query = buildQuery<BankAttributes>(
      // Expandimos ambos objetos en el buildQuery
      ...searchByCodeQueryObject,
      ...searchByNameQueryObject,

      // Ordenamiento y paginación
      orderBy(
        options.sort ?? "name",
        options.sortDirection === "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    const API_URL: string = apiUrl +
      `api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IPaginationResult<BankAttributes>>(API_URL);
  }

  /**
 * Find to combo box
 * @returns Data
 */

  findToComboBox(): Observable<IRequestResult<BankAttributes[]>> {
    const query = buildQuery<BankAttributes>(
      where("statusId", StatusEnum.ACTIVE),
      select(["name", "code", "externalKeys"]),
      orderBy("code", OrderVariant.Asc)
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/filter?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IRequestResult<BankAttributes[]>>(API_URL);
  }
}
